import type { Navigator, Screen } from "../app/screen";
import { Api, ApiError } from "../net/api";
import { pageShell, note } from "../ui/page";
import { REALM_COLOUR } from "../card-frames.generated";
import { loadCardNames, cardName, cardRealm, cardRecord } from "../match/cards";
import { cardEl } from "../match/card";
import type { RuntimeObject } from "../match/state";
import { audio } from "../fx/audio";

/**
 * THE HERO CAMPAIGN - the mode that teaches a class, and the only place the game says WHICH card
 * you win.
 *
 * 🚨 **A WHOLE SECOND CAMPAIGN MODE WAS SERVED AND UNREACHABLE, AND ALEX FOUND IT BY ASKING.**
 * `GET /api/v1/HeroCampaign/Progress` and `POST /StartStage` have both been mapped since the server
 * was built; **no file under `client-web/src` mentioned either**. Eight stages, each naming an
 * opponent, that opponent's hero power, its own bot deck and explicit `rewardCardIds`.
 *
 *     *"I don't know what I don't know, so you must have the tools to scrape all the knowledge,
 *     modes, game play, rules, logic, etc from the original."*  - Alex, 2026-08-14
 *
 * `tools/verify_route_coverage.py` is now the instrument; this screen is the fix.
 *
 * ⭐ **THE REWARD IS DRAWN AS THE ACTUAL CARD, NOT NAMED IN TEXT.** Alex, on the deck builder:
 * *"they must be able to see the beautiful images on the cards, not just text."* It is doubly true
 * here - the card IS the reason to play the stage, so a player must see what they are playing for.
 * Same `cardEl` the board and the builder use; a second way of drawing a card is a second owner.
 *
 * ⭐ **NO DECK PICKER, AND THAT IS THE DESIGN, NOT AN OMISSION.** The original's own request shapes
 * split it by mode:
 *
 *     CampaignStartStageModel     { Quadrant, StageIndex, DeckId, IsHeroic }   <- your deck
 *     HeroCampaignStartStageModel { CampaignId, Stage }                        <- no deck
 *
 * The hero's list is fixed, so every player fights these eight stages under identical conditions.
 * Alex asked for exactly that: *"I lean on limited so that it keeps the difficulty level even for
 * all players and also for us the house we can maintain the difficulty level even."* It is also why
 * this is the right mode to hang a card reward on.
 *
 * ⚠️ **ONE CAMPAIGN EXISTS, NOT FOUR.** The client's `HeroModeHeroDefinition` lists four, but only
 * `warden_grove_awakens` has stages - and the card database backs only it (`heroClass:
 * WardenOfTheGrove` names its five reward cards). The other three classes own nothing but the
 * 0-mana tokens their hero powers summon. Authoring them needs NEW CARDS, which moves the mechanics
 * lock. So this screen renders what is served and does not invent a menu of three dead entries.
 */

interface Stage {
  stage?: number;
  title?: string;
  opponent?: string;
  opponentPowerName?: string;
  opponentPowerDescription?: string;
  difficulty?: string;
  botDeckId?: string;
  rewardCardIds?: string[];
  rewardCardNames?: string[];
  isUnlocked?: boolean;
  isCompleted?: boolean;
}

interface Progress {
  campaignId?: string;
  title?: string;
  subtitle?: string;
  heroId?: string;
  heroClassName?: string;
  highestStageCompleted?: number;
  stages?: Stage[];
}

export const HeroCampaignScreen: Screen = {
  id: "herocampaign",

  mustOffer: ["BACK"],

  async mount(host: HTMLElement, nav: Navigator): Promise<void> {
    const shell = pageShell(nav, "THE GROVE AWAKENS", "learn a class, win its cards");
    host.append(shell.el);

    const root = document.createElement("div");
    root.className = "herocamp";
    shell.body.append(root);
    root.append(note("Reading the trial..."));

    await loadCardNames();

    let data: Progress;
    try {
      data = await Api.heroCampaignProgress() as Progress;
    } catch (e) {
      root.replaceChildren(note(e instanceof ApiError ? e.body || e.message : String(e), true));
      return;
    }

    const stages = (data.stages ?? []).slice().sort((a, b) => (a.stage ?? 0) - (b.stage ?? 0));
    if (!stages.length) {
      root.replaceChildren(note("This trial has no stages.", true));
      return;
    }

    function draw(): void {
      root.replaceChildren();

      const intro = document.createElement("p");
      intro.className = "herocamp__intro";
      intro.textContent = String(data.subtitle ?? "");

      const done = stages.filter((s) => s.isCompleted).length;
      const head = document.createElement("p");
      head.className = "herocamp__progress";
      head.textContent = `${done} of ${stages.length} cleared`;

      // ⚠️ Say the fixed-deck rule OUT LOUD. A player who has just built a deck and cannot choose it
      // will read a missing picker as a bug, and "the game is broken" is a much worse first
      // impression than a rule they were told.
      const rule = document.createElement("p");
      rule.className = "herocamp__rule";
      rule.textContent = "Everyone plays these trials with the hero's own deck, so the challenge "
        + "is identical for every player. Cards you win here are yours to keep.";

      const list = document.createElement("div");
      list.className = "herocamp__stages";

      for (const st of stages) {
        const locked = st.isUnlocked === false;
        const row = document.createElement("div");
        // The content-coverage contract, same as the realms screen. A machine channel.
        row.dataset["herostage"] = String(st.stage ?? 0);
        row.className = "herocamp__stage"
          + (st.isCompleted ? " herocamp__stage--done" : "")
          + (locked ? " herocamp__stage--locked" : "");

        const n = document.createElement("span");
        n.className = "herocamp__no";
        n.textContent = String(st.stage ?? "");

        const body = document.createElement("div");
        body.className = "herocamp__body";

        const t = document.createElement("span");
        t.className = "herocamp__title";
        t.textContent = String(st.title ?? "");
        const diff = document.createElement("span");
        diff.className = "herocamp__diff";
        diff.textContent = String(st.difficulty ?? "");
        t.append(diff);

        // The opponent AND its power. The stage authored both, and a player who cannot see the
        // opponent's power cannot plan against it - which is the whole lesson of a teaching mode.
        const opp = document.createElement("span");
        opp.className = "herocamp__opp";
        opp.textContent = `${st.opponent ?? "Opponent"} - ${st.opponentPowerName ?? ""}`;
        const pow = document.createElement("span");
        pow.className = "herocamp__pow";
        pow.textContent = String(st.opponentPowerDescription ?? "");

        body.append(t, opp, pow);

        // ⭐ THE PRIZE, AS THE CARD ITSELF.
        const prize = document.createElement("div");
        prize.className = "herocamp__prize";
        for (const id of st.rewardCardIds ?? []) {
          const rec = cardRecord(id);
          const view: RuntimeObject = {
            DataId: id,
            Mana: { Current: Number(rec?.mana ?? 0) },
            Attack: { Current: Number(rec?.attack ?? 0) },
            Hp: { Current: Number(rec?.hp ?? 0) },
          };
          const holder = document.createElement("div");
          holder.className = "herocamp__prizecard";
          const realm = cardRealm(id);
          if (realm && REALM_COLOUR[realm]) holder.style.setProperty("--realm", REALM_COLOUR[realm]!);
          const el = cardEl(view, { mini: true });
          el.title = cardName(id);
          holder.append(el);
          if (st.isCompleted) {
            const got = document.createElement("span");
            got.className = "herocamp__won";
            got.textContent = "WON";
            holder.append(got);
          }
          prize.append(holder);
        }

        const go = document.createElement("button");
        go.type = "button";
        go.className = "utilbtn herocamp__play";
        // ⚠️ A cleared stage can be REPLAYED but pays nothing - the server enforces win-once, so the
        // button must not promise a card it will not hand over.
        go.dataset["label"] = locked ? "LOCKED" : st.isCompleted ? "REPLAY" : "PLAY";
        go.textContent = go.dataset["label"]!;
        go.disabled = locked;
        go.title = locked
          ? "Clear the stage before it to open this one"
          : st.isCompleted
            ? "Already cleared - replay it for practice; the card is only won once"
            : `Win to keep ${(st.rewardCardNames ?? []).join(" and ") || "a card"}`;
        go.addEventListener("click", () => void start(st));

        row.append(n, body, prize, go);
        list.append(row);
      }

      root.append(intro, head, rule, list);
    }

    async function start(st: Stage): Promise<void> {
      const busy = note(`Entering ${st.title ?? "the trial"}...`);
      root.append(busy);
      try {
        // ⚠️ NO deckId. The hero's list is fixed - see the note at the top of this file.
        await Api.heroCampaignStartStage({
          campaignId: String(data.campaignId ?? ""),
          heroId: String(data.heroId ?? ""),
          stageIndex: st.stage ?? 1,
        });
        audio.play("queue");
        nav.go("match");
      } catch (e) {
        busy.replaceWith(note(e instanceof ApiError ? e.body || e.message : String(e), true));
      }
    }

    draw();
  },
};
